// presence — admin-only org-wide activity tracking on the dashboard.
//
// While the user has interacted with the page (mouse, key, scroll, touch)
// inside the last IDLE_TIMEOUT_MS window AND the tab is visible, we fire a
// {type:"presence:heartbeat"} frame over the existing dashboard WS every
// HEARTBEAT_INTERVAL_MS. The server tracks open sessions, closes them on
// idle, and broadcasts the active-user list to admin dashboards via the
// `presence:admin` topic. Every authenticated user heartbeats — only
// admins receive the live list (and only admins can see the Usage page).

import { sendMessage } from "./dashboardEvents";

const HEARTBEAT_INTERVAL_MS = 30_000;
const IDLE_TIMEOUT_MS = 60_000;

interface PresenceController {
  stop(): void;
}

let active: PresenceController | null = null;

const INPUT_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
  "pointerdown",
] as const;

export function startPresence(): PresenceController {
  if (active) return active;

  let lastInputAt = Date.now();
  let lastHeartbeatSent = 0;

  function onInput(): void {
    lastInputAt = Date.now();
  }

  function tick(): void {
    if (document.hidden) return;
    const now = Date.now();
    if (now - lastInputAt > IDLE_TIMEOUT_MS) return;
    // We send at most one heartbeat per HEARTBEAT_INTERVAL_MS even if the
    // tick fires more often. Server-side bumps are cheap (single UPDATE
    // by primary key) but no point being chatty.
    if (now - lastHeartbeatSent < HEARTBEAT_INTERVAL_MS - 1_000) return;
    lastHeartbeatSent = now;
    const orgId = localStorage.getItem("active_org_id") ?? null;
    sendMessage({ type: "presence:heartbeat", orgId });
  }

  // Emit one heartbeat on start so the user shows up immediately instead
  // of waiting up to 30 s for the first interval tick.
  lastInputAt = Date.now();
  tick();

  for (const ev of INPUT_EVENTS) {
    document.addEventListener(ev, onInput, { passive: true, capture: true });
  }
  // Treat tab-focus as input — most users come back from a different tab and
  // immediately click somewhere, but we want them counted active right away.
  function onVisibility(): void {
    if (!document.hidden) {
      lastInputAt = Date.now();
      tick();
    }
  }
  document.addEventListener("visibilitychange", onVisibility);

  const intervalId = window.setInterval(tick, HEARTBEAT_INTERVAL_MS);

  active = {
    stop(): void {
      window.clearInterval(intervalId);
      for (const ev of INPUT_EVENTS) {
        document.removeEventListener(ev, onInput, { capture: true });
      }
      // Must mirror the add above — a missed removal here leaks the listener
      // and lets a tab-refocus fire a zombie heartbeat after stop().
      document.removeEventListener("visibilitychange", onVisibility);
      active = null;
    },
  };
  return active;
}
